import {
  Frequency,
  WithdrawalMode,
  WithdrawFeeTypes,
} from '@generated/graphql';
import { EXCEEDS_AVAILABLE_THRESHOLD } from './withdrawal.constants';

export type WithdrawalFee = {
  type: string;
  amountCents: number;
};

type GeneratedWithdrawFees =
  | {
      calculateWithdrawFees?: {
        withdrawFees?:
          | ({
              type?: string | null;
              amountCents?: number | null;
            } | null)[]
          | null;
      } | null;
    }
  | undefined;

export type NetFeesReport = {
  accountId: string | undefined;
  netCents: number;
  fees: WithdrawalFee[];
};

export const toWithdrawalFees = (data: GeneratedWithdrawFees) =>
  data?.calculateWithdrawFees?.withdrawFees?.flatMap((fee) =>
    fee?.type && typeof fee.amountCents === 'number'
      ? [{ type: fee.type, amountCents: fee.amountCents }]
      : [],
  );

const isImmediateOneTime = (
  frequency: string | null | undefined,
  scheduledDate: Date | string | null | undefined,
) => (!frequency || frequency === Frequency.OneTime) && !scheduledDate;

export const isWithdrawalModeSelectable = ({
  flagEnabled,
  optionEnabled,
  frequency,
  scheduledDate,
  isFutureDated,
  isWithdrawAll,
  isMultiCurrency,
}: {
  flagEnabled: boolean;
  optionEnabled: boolean;
  frequency: string | null | undefined;
  scheduledDate: Date | string | null | undefined;
  isFutureDated: boolean;
  isWithdrawAll: boolean;
  isMultiCurrency: boolean;
}) =>
  flagEnabled &&
  optionEnabled &&
  isImmediateOneTime(frequency, scheduledDate) &&
  !isFutureDated &&
  !isWithdrawAll &&
  !isMultiCurrency;

export const isWithdrawalFeatureActive = ({
  flagEnabled,
  optionEnabled,
  storedMode,
  netAmountCents,
}: {
  flagEnabled: boolean;
  optionEnabled: boolean;
  storedMode: WithdrawalMode | undefined;
  netAmountCents: number | undefined;
}) =>
  (flagEnabled && optionEnabled) ||
  storedMode !== undefined ||
  netAmountCents !== undefined;

export const toDefaultWithdrawalMode = (optionValue: unknown) =>
  optionValue === WithdrawalMode.Net
    ? WithdrawalMode.Net
    : WithdrawalMode.Gross;

export const resolveWithdrawalMode = ({
  selectable,
  storedMode,
  defaultMode,
}: {
  selectable: boolean;
  storedMode: WithdrawalMode | undefined;
  defaultMode: WithdrawalMode | undefined;
}) =>
  selectable && (storedMode ?? defaultMode) === WithdrawalMode.Net
    ? WithdrawalMode.Net
    : WithdrawalMode.Gross;

export const deriveGrossCents = ({
  netCents,
  fees,
}: {
  netCents: number;
  fees: WithdrawalFee[];
}) =>
  netCents +
  (fees.find((fee) => fee.type === WithdrawFeeTypes.WithholdingTax)
    ?.amountCents ?? 0);

export const resolveGrossCents = ({
  report,
  accountId,
  netCents,
}: {
  report: NetFeesReport | null;
  accountId: string | undefined;
  netCents: number | undefined;
}) => {
  if (
    !report ||
    netCents === undefined ||
    report.accountId !== accountId ||
    report.netCents !== netCents
  ) {
    return undefined;
  }

  return deriveGrossCents({ netCents, fees: report.fees });
};

export const isFullTransfer = (
  amountCents: number,
  availableFundsToWithdrawCents: number,
) =>
  amountCents >
  (availableFundsToWithdrawCents || 0) * EXCEEDS_AVAILABLE_THRESHOLD;

export const resolveRequestedAmountCents = ({
  isNetWithdrawal,
  netAmountCents,
  grossAmountCents,
}: {
  isNetWithdrawal: boolean;
  netAmountCents: number | undefined;
  grossAmountCents: number;
}) => (isNetWithdrawal ? (netAmountCents ?? 0) : grossAmountCents);

// The custodian withholds the tax from the gross and charges the deregistration fee
// separately, so a Net withdrawal pays out exactly the requested net.
export const resolveEstimateInputs = ({
  isNetWithdrawal,
  netAmountCents,
  grossAmountCents,
  feesToDeductCents,
}: {
  isNetWithdrawal: boolean;
  netAmountCents: number | undefined;
  grossAmountCents: number;
  feesToDeductCents: number;
}) => ({
  baseCents: resolveRequestedAmountCents({
    isNetWithdrawal,
    netAmountCents,
    grossAmountCents,
  }),
  deductedFeesCents: isNetWithdrawal ? 0 : feesToDeductCents,
});

export type WithdrawalAmountDisplay = 'money' | 'loading' | 'none';

export const resolveWithdrawalAmountDisplay = ({
  isNetWithdrawal,
  amountCents,
  isGrossPending,
  isNetFeesUnavailable,
}: {
  isNetWithdrawal: boolean;
  amountCents: number;
  isGrossPending: boolean;
  isNetFeesUnavailable: boolean;
}): WithdrawalAmountDisplay => {
  if (!isNetWithdrawal || amountCents > 0) return 'money';

  return isGrossPending && !isNetFeesUnavailable ? 'loading' : 'none';
};

export const buildCalculateWithdrawFeesInput = ({
  accountId,
  amountCents,
  isNetWithdrawal,
  isFullWithdrawal,
}: {
  accountId: string;
  amountCents: number;
  isNetWithdrawal: boolean;
  isFullWithdrawal: boolean;
}) => ({
  accountId,
  amountCents,
  isfullWithdraw: !isNetWithdrawal && isFullWithdrawal,
  ...(isNetWithdrawal ? { net: true } : {}),
});

export const toWithdrawalValidationOptions = <
  T extends { amountCents?: unknown },
>(
  options: T,
  isNetWithdrawal: boolean,
): T =>
  isNetWithdrawal
    ? {
        ...options,
        amountCents: undefined,
        netAmountCents: options.amountCents,
      }
    : options;
